import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { WorkOrdersStore } from './state/workorders.store';
import { WorkOrdersService } from '../api/services/WorkOrdersService';
import { WorkOrdersQuery } from './state/workorders.query';
import { EmployeeVisitsQuery } from 'src/employee-visits/state/employeeVisits.query';
import { EmployeeVisitsStore } from 'src/employee-visits/state/employeeVisits.store';
import { DateService } from 'src/app/shared/services/date.service';
import {
  AssignWorkOrderSpecialistsCommand,
  WorkOrderIsNotMineCommand,
  EmployeeVisitDto,
  EmployeeVisitToCreate,
  CreateWorkOrderEmployeeVisitsCommand,
  EmployeeVisitToUpdate,
  UpdateWorkOrderEmployeeVisitsCommand,
  RemoveWorkOrderEmployeeVisitsCommand,
  UpdateWorkOrderCommand,
  OptimizeWorkOrderCommand,
  RejectWorkOrderCommand,
  AttachedFileDto,
} from 'src/api';

@Injectable({
  providedIn: 'root',
})
export class WorkOrdersRepository {
  constructor(
    private workOrdersStore: WorkOrdersStore,
    private workOrdersService: WorkOrdersService,
    private workOrdersQuery: WorkOrdersQuery,
    private employeeVisitsStore: EmployeeVisitsStore,
    private employeeVisitsQuery: EmployeeVisitsQuery
  ) {
  }

  loadWorkOrders = async () => {
    if (!this.workOrdersQuery.getHasCache()) {
      const workOrders = await firstValueFrom(this.workOrdersService.getVWorkOrders1('1'));
      this.workOrdersStore.set(workOrders);
    }
  };

  getWorkOrder = async (id: number) => {
    const workOrder = await firstValueFrom(this.workOrdersService.getVWorkOrders(id, '1'));
    this.workOrdersStore.upsert(id, workOrder);
  };

  updateStateFilter(newState?: string) {
    this.workOrdersStore.update((s: { filters: any }) => ({
      filters: { ...s.filters, state: newState },
    }));
  }

  updateSearchTerm(searchTerm?: string) {
    this.workOrdersStore.update((s: { filters: any }) => ({
      filters: { ...s.filters, searchTerm: searchTerm },
    }));
  }

  async assignSpecialists(workOrderId: number, specialistIds: number[]) {
    const command = {
      workOrderId: workOrderId,
      specialistIds: specialistIds,
    } as AssignWorkOrderSpecialistsCommand;

    await firstValueFrom(this.workOrdersService.postVWorkOrdersAssignWorkOrderSpecialists('1', command));

    const workOrderState = specialistIds.length > 0 ? 'Assigned' : 'Unassigned';
    this.workOrdersStore.update(workOrderId, {
      specialistIds,
      workOrderState: workOrderState,
    });
  }

  async setWorkOrderAsNotMine(workOrderId: number, suggestedSupervisor?: number) {
    const command = {
      workOrderId: workOrderId,
      suggestedSupervisorId: suggestedSupervisor,
    } as WorkOrderIsNotMineCommand;

    await firstValueFrom(this.workOrdersService.postVWorkOrdersWorkOrderIsNotMine('1', command));

    this.workOrdersStore.remove(workOrderId);
  }

  async createWorkOrderEmployeeVisits(workOrderId: number, employeeVisits: EmployeeVisitDto[]) {
    const employeeVisitsToCreate = employeeVisits.map((visit) => {
      return {
        employeeId: visit.employeeId,
        startDate: visit.startDate,
        endDate: visit.endDate,
      } as EmployeeVisitToCreate;
    });

    const command = {
      workOrderId: workOrderId,
      employeeVisits: employeeVisitsToCreate,
    } as CreateWorkOrderEmployeeVisitsCommand;

    const savedEmployeeVisits = await firstValueFrom(
      this.workOrdersService.postVWorkOrdersCreateWorkOrderEmployeeVisits('1', command)
    );

    const hasVisits = this.employeeVisitsQuery.hasEntity((ev) => ev.workOrderId == workOrderId);
    if (!hasVisits) {
      const newState = 'Working';
      this.workOrdersStore.update(workOrderId, {
        workOrderState: newState,
      });
    }

    this.employeeVisitsStore.add(savedEmployeeVisits);
  }

  async updateRemoveWorkOrderEmployeeVisits(
    workOrderId: number,
    visitsToUpdate: EmployeeVisitDto[],
    visitsToRemove: number[]
  ) {
    if (visitsToUpdate.length > 0) {
      const employeeVisitsToUpdate = visitsToUpdate.map((visit) => {
        return {
          id: visit.id,
          employeeId: visit.employeeId,
          startDate: visit.startDate,
          endDate: visit.endDate,
        } as EmployeeVisitToUpdate;
      });

      const command: UpdateWorkOrderEmployeeVisitsCommand = {
        workOrderId: workOrderId,
        employeeVisits: employeeVisitsToUpdate,
      };

      const updatedEmployeeVisits = await firstValueFrom(
        this.workOrdersService.postVWorkOrdersUpdateWorkOrderEmployeeVisits('1', command)
      );

      this.employeeVisitsStore.upsertMany(updatedEmployeeVisits);
    }

    if (visitsToRemove.length > 0) {
      this.removeWorkOrderEmployeeVisits(workOrderId, visitsToRemove);
    }
  }

  async removeWorkOrderEmployeeVisits(workOrderId: number, visitsToRemove: number[]) {
    const command: RemoveWorkOrderEmployeeVisitsCommand = {
      workOrderId: workOrderId,
      employeeVisitIdsToRemove: visitsToRemove,
    };

    await firstValueFrom(this.workOrdersService.postVWorkOrdersRemoveWorkOrderEmployeeVisits('1', command));
    this.employeeVisitsStore.remove(visitsToRemove);

    const employeeHasVisits = this.employeeVisitsQuery.hasEntity((ev) => ev.workOrderId == workOrderId);
    if (!employeeHasVisits) {
      const newState = 'Assigned';
      this.workOrdersStore.update(workOrderId, {
        workOrderState: newState,
      });
    }
  }

  async finishWorkOrder(
    workOrderId: number,
    employeeSignature: Blob,
    clientSignature: Blob,
    attachedFiles: File[],
    observations: string,
    clientSigner: string
  ) {
    await firstValueFrom(
      this.workOrdersService.postVWorkOrdersFinishWorkOrder('1', {
        WorkOrderId: workOrderId,
        Signatures: [employeeSignature, clientSignature],
        AttachedFiles: attachedFiles,
        Observations: observations,
        ClientSignerName: clientSigner,
      })
    );

    //add files to storage
    if (attachedFiles.length > 0) {
      let attachedFilesDto: AttachedFileDto[] = [];
      let storedFiles = this.workOrdersQuery.getEntity(workOrderId)?.attachedFiles;

      await attachedFiles.reduce(async (a, file) => {
        // Wait for the previous item to finish processing
        await a;
        // Process this item
        const fileDto = await this.createAttachedFileToStore(file);
        attachedFilesDto.push(fileDto);
      }, Promise.resolve());

      this.workOrdersStore.update(workOrderId, {
        attachedFiles: [...(storedFiles ? storedFiles : []), ...attachedFilesDto],
      });
    }

    //need msal service to work
    const currentUser = 'Pedro';
    const newState = 'Finished';

    this.workOrdersStore.update(workOrderId, {
      workOrderState: newState,
      clientSignerName: clientSigner,
      employeeSignerName: currentUser,
    });
  }

  async rejectWorkOrder(workOrderId: number) {
    const newState = 'Working';
    const command: RejectWorkOrderCommand = {
      workOrderId: workOrderId,
    };

    await firstValueFrom(this.workOrdersService.postVWorkOrdersRejectWorkOrder('1', command));

    //update attached files to previous in store
    const attachedFiles = this.workOrdersQuery.getEntity(workOrderId)?.attachedFiles;
    if (attachedFiles && attachedFiles.length > 0) {
      let storedFiles: AttachedFileDto[] = [];

      attachedFiles.forEach((file) => {
        const fileToAttach = {
          name: file.name,
          modifiedDate: file.modifiedDate,
          content: file.content,
          size: file.size,
          isPreviouslyAttachedFile: true,
        } as AttachedFileDto;

        storedFiles.push(fileToAttach);
      });

      this.workOrdersStore.update(workOrderId, {
        attachedFiles: storedFiles,
      });
    }

    //update store
    this.workOrdersStore.update(workOrderId, {
      workOrderState: newState,
    });
  }

  async validateWorkOrder(workOrderId: number) {
    const newState = 'Validated';
    const command: UpdateWorkOrderCommand = {
      workOrderId: workOrderId,
      workOrderState: newState,
    };

    await firstValueFrom(this.workOrdersService.putVWorkOrders('1', command));

    const closeDate = DateService.convertDateToString(new Date());
    this.workOrdersStore.update(workOrderId, {
      workOrderState: newState,
      closeDate: closeDate,
    });
  }

  async updateWorkOrderExtraTime(workOrderId: number, managementTime: number, transportTime: number) {
    const command: UpdateWorkOrderCommand = {
      workOrderId: workOrderId,
      managementExtra: managementTime,
      transportExtra: transportTime,
    };

    await firstValueFrom(this.workOrdersService.putVWorkOrders('1', command));

    this.workOrdersStore.update(workOrderId, {
      managementExtra: managementTime,
      transportExtra: transportTime,
    });
  }

  async optimizeWorkOrder(workOrderId: number) {
    const command: OptimizeWorkOrderCommand = {
      workOrderId: workOrderId,
    };

    await firstValueFrom(this.workOrdersService.postVWorkOrdersOptimizeWorkOrder('1', command));

    const newState = 'Validated';
    const closeDate = DateService.convertDateToString(new Date());
    this.workOrdersStore.update(workOrderId, {
      workOrderState: newState,
      closeDate: closeDate,
    });
  }

  loadWorkOrderAttachedFiles = async (workOrderId: number) => {
    const attachedFiles = await firstValueFrom(this.workOrdersService.getVWorkOrdersAttachedFiles(workOrderId, '1'));
    this.workOrdersStore.update(workOrderId, {
      attachedFiles: attachedFiles,
    });
  };

  async createAttachedFileToStore(file: File) {
    const base64 = await this.blobToBase64(file);
    return {
      name: file.name,
      modifiedDate: new Date().toString(),
      content: base64,
      size: Math.floor(file.size / 1024),
      isPreviouslyAttachedFile: false,
    } as AttachedFileDto;
  }

  blobToBase64 = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
}
